import React from 'react';
import { Link } from 'react-router-dom';
import { Cloud, ArrowRight, Settings, Calendar, Sparkles, BarChart3 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const HowItWorksPage = () => {
  return (
    <>
      {/* Header */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">
            How Keyless PMS{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              works
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            From setup to daily operations—see how easy it is to run your property with Keyless PMS
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto space-y-24">
          {/* Step 1 */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-6 text-white text-2xl font-bold">
                1
              </div>
              <h2 className="text-3xl font-bold mb-4">Set up in a few minutes</h2>
              <p className="text-lg text-slate-600 mb-4">
                Getting started with Keyless PMS takes about 10 minutes. Our setup wizard guides you through adding your property details, creating your rooms, setting your rates, and configuring taxes. You can customize room types, set up rate plans for different seasons, and define your cancellation policies.
              </p>
              <p className="text-lg text-slate-600 mb-4">
                Once your property is configured, invite your team members and assign them roles—Manager, Front Desk, Housekeeping, or Maintenance. Each role sees only what they need, keeping the interface simple for everyone.
              </p>
              <p className="text-lg text-slate-600">
                You can always come back and adjust settings later. Nothing is locked in.
              </p>
            </div>
            <Card className="bg-gradient-to-br from-cyan-50 to-blue-50">
              <CardContent className="pt-6">
                <div className="aspect-video bg-white rounded-lg flex items-center justify-center">
                  <Settings size={64} className="text-cyan-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Step 2 */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <Card className="bg-gradient-to-br from-blue-50 to-purple-50 order-2 md:order-1">
              <CardContent className="pt-6">
                <div className="aspect-video bg-white rounded-lg flex items-center justify-center">
                  <Calendar size={64} className="text-blue-600" />
                </div>
              </CardContent>
            </Card>
            <div className="order-1 md:order-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-6 text-white text-2xl font-bold">
                2
              </div>
              <h2 className="text-3xl font-bold mb-4">Manage reservations & front desk</h2>
              <p className="text-lg text-slate-600 mb-4">
                The calendar is your command center. It displays all your rooms across dates in an interactive tape chart format. Each reservation appears as a colored block showing the guest name, room number, and booking dates. You can see arrivals, departures, and in-house guests at a glance.
              </p>
              <p className="text-lg text-slate-600 mb-4">
                Create new bookings in seconds. Click any empty space on the calendar, or use the "New Reservation" button. Enter guest details, select dates, choose the room, and set the rate. The system calculates taxes automatically.
              </p>
              <p className="text-lg text-slate-600">
                When guests arrive, the Today view shows you exactly who's checking in and out. Click "Check In" next to their name—that's it. Check-out is just as fast. No complicated workflows, no extra clicks.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-6 text-white text-2xl font-bold">
                3
              </div>
              <h2 className="text-3xl font-bold mb-4">Stay on top of housekeeping & maintenance</h2>
              <p className="text-lg text-slate-600 mb-4">
                Housekeeping staff see their daily task list as soon as they log in. Each room shows its current status—Clean, Dirty, In Progress, or Out of Order. When a room is marked Clean, it automatically becomes available for new check-ins.
              </p>
              <p className="text-lg text-slate-600 mb-4">
                Your housekeeping supervisor can reassign rooms, add notes, and track which staff member cleaned which room. Everything updates in real time so the front desk always knows which rooms are ready to sell.
              </p>
              <p className="text-lg text-slate-600">
                For maintenance issues, create a work order with a few clicks. Describe the problem, set the priority (Low, Medium, High, Urgent), and assign it to a staff member. Maintenance staff can update the status as they work, and the front desk is notified when the room is back in service.
              </p>
            </div>
            <Card className="bg-gradient-to-br from-purple-50 to-pink-50">
              <CardContent className="pt-6">
                <div className="aspect-video bg-white rounded-lg flex items-center justify-center">
                  <Sparkles size={64} className="text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Step 4 */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <Card className="bg-gradient-to-br from-pink-50 to-orange-50 order-2 md:order-1">
              <CardContent className="pt-6">
                <div className="aspect-video bg-white rounded-lg flex items-center justify-center">
                  <BarChart3 size={64} className="text-pink-600" />
                </div>
              </CardContent>
            </Card>
            <div className="order-1 md:order-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-orange-600 flex items-center justify-center mb-6 text-white text-2xl font-bold">
                4
              </div>
              <h2 className="text-3xl font-bold mb-4">Understand your performance</h2>
              <p className="text-lg text-slate-600 mb-4">
                The Dashboard gives you the numbers that matter: occupancy rate, total revenue, average daily rate (ADR), and revenue per available room (RevPAR). These update in real time based on your bookings and check-ins.
              </p>
              <p className="text-lg text-slate-600 mb-4">
                The Forecasting page shows you what's coming. See your expected occupancy for the next 30, 60, or 90 days. This helps you plan staffing, adjust rates, and spot slow periods before they happen.
              </p>
              <p className="text-lg text-slate-600">
                When you need to share data with ownership or your accountant, use the Export Reports page. Generate CSV files with reservation details, occupancy statistics, maintenance logs, or guest information—filtered by date range and property.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-cyan-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Get started in minutes—no installation required</h2>
          <p className="text-xl mb-8 opacity-90">
            Keyless PMS is 100% cloud-based. There's nothing to download or install. Just create your account, set up your property, and start managing bookings. Your team can access it from any computer or tablet with a web browser.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Link to="/signup">
              <Button size="lg" className="bg-white text-cyan-700 hover:bg-slate-100 w-full sm:w-auto">
                Start your free trial
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
              Book a demo
            </Button>
          </div>
          <p className="text-sm opacity-75">
            14-day free trial • No credit card required • Cancel anytime
          </p>
        </div>
      </section>
    </>
  );
};

export default HowItWorksPage;